"use client";

import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";

import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import { EmbedProvider } from "@/lib/utils/lomdaV3/types";
import { toProviderEmbed } from "../../lib/embedUrl";
import { useEmbedFraming } from "../../api/useEmbedFraming";

/** Author-only: warns while typing a link that learners will get the "Open in new tab" card. */
export function EmbedBlockedWarning({
    provider,
    value,
}: {
    provider: EmbedProvider;
    value: string;
}) {
    const { dir } = useLocale();
    const [settled, setSettled] = useState(value.trim());

    useEffect(() => {
        const id = setTimeout(() => setSettled(value.trim()), 400);
        return () => clearTimeout(id);
    }, [value]);

    const src = settled ? toProviderEmbed(provider, settled) : "";
    const framing = useEmbedFraming(src, Boolean(src));
    if (framing !== "blocked") return null;

    return (
        <p
            role="status"
            dir={dir}
            className="flex items-start gap-2 rounded-md bg-amber-50 p-2.5 text-start text-sm text-amber-700"
        >
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            {t("lomda-v3-editor.embed.blocked.author-warning")}
        </p>
    );
}
